import { AlertTriangle } from 'lucide-react'
import { coldroomChipLabel } from '@/lib/coldroomPlacement'

// The rich pallet "card body" — summary tags, SSC barcode and the
// cartons/kg/pack/dispatch lines — shared by the Runs detail "Pallets" tab
// and the All-pallets cards so both show the same field set as the by-run
// pallet card without copy-pasting ~25 lines of JSX three times.
//
// Presentational only: the caller owns the wrapper, the pallet-id header and
// the status badge (those differ per surface — checkbox + link on Runs, a
// navigate button on All-pallets). This renders the info that sits underneath.

export interface PalletSummaryRow {
  pallet_type?:               string | null
  spec?:                      string | null
  grade?:                     string | null
  size_code?:                 string | null
  mark?:                      string | null
  regime_code?:               string | null
  target_market?:             string | null
  destination?:               string | null
  pick_no?:                   string | null
  pack_type?:                 string | null
  carton_count?:              number | null
  net_weight_kg?:             number | null
  coldroom_grid?:             string | null
  coldroom_overflow?:         boolean | null
  loading_bay_lane?:          string | null
  line_count?:                number | null
  ssc_id?:                    string | null
  parent_pallet_id?:          string | null
  intake_no?:                 string | null
  consignment_no?:            string | null
  truck_consignment_number?:  string | null
  ppecb_consignment_number?:  string | null
  mission_number?:            string | null
  mission_dispatched_at?:     string | null
  packed_at?:                 string | null
  born_date?:                 string | null
}

function packedAgeLabel(packedAt: string): string {
  const ms = Date.now() - new Date(packedAt).getTime()
  const hours = ms / 3_600_000
  if (hours < 1)  return `${Math.max(1, Math.round(hours * 60))}m ago`
  if (hours < 48) return `${Math.round(hours)}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export default function PalletSummaryInfo({
  pallet,
  variety,
}: {
  pallet: PalletSummaryRow
  // Run variety — not stored on the pallet row itself, passed by the caller.
  variety?: string | null
}) {
  const isExport = /export/i.test(pallet.pallet_type ?? pallet.spec ?? '')

  const tags = [
    pallet.pallet_type ?? pallet.spec,
    variety,
    pallet.grade ? `Grade ${pallet.grade}` : null,
    pallet.size_code,
    pallet.mark,
    pallet.regime_code,
    // Target market shows on both channels now (mig 1002); destination is local-only.
    pallet.target_market,
    isExport ? null : pallet.destination,
    pallet.pick_no ? `Pick ${pallet.pick_no}` : null,
    coldroomChipLabel(pallet),   // 'Grid A3' / 'Overflow' / nothing
    pallet.loading_bay_lane ? `Lane ${pallet.loading_bay_lane}` : null,
    (pallet.line_count ?? 1) > 1 ? `${pallet.line_count} lines` : null,
  ].filter(Boolean) as string[]

  const hasDispatchChips =
    pallet.intake_no || pallet.consignment_no || pallet.truck_consignment_number ||
    pallet.ppecb_consignment_number || pallet.mission_number

  return (
    <>
      {tags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {tags.map(t => (
            <span key={t} className="text-xs surface-raised text-fg-secondary rounded-full px-2 py-0.5">{t}</span>
          ))}
        </div>
      )}

      {/* SSC barcode — large + monospace so it's readable from a phone.
          Repacked pallets start with a NULL ssc_id (must be reprinted), so
          surface a warning rather than going silent. */}
      {pallet.ssc_id ? (
        <p className="font-mono text-base sm:text-lg font-semibold text-emerald-200 tracking-wider mt-1 break-all">
          {pallet.ssc_id}
        </p>
      ) : pallet.parent_pallet_id ? (
        <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-amber-900/40 text-amber-200 border border-amber-700/40">
          <AlertTriangle size={11} /> Needs SSCC — reprint label
        </p>
      ) : null}

      <div className="flex gap-3 mt-0.5 text-xs text-fg-muted flex-wrap">
        {pallet.carton_count != null && <span>{pallet.carton_count} cartons</span>}
        {pallet.net_weight_kg != null && <span>{pallet.net_weight_kg.toFixed(1)} kg net</span>}
        {pallet.pack_type && <span>{pallet.pack_type}</span>}
        {pallet.destination && <span>{pallet.destination}</span>}
        {pallet.packed_at && <span className="text-fg-muted">packed {packedAgeLabel(pallet.packed_at)}</span>}
        {/* born_date = the fruit's true age anchor (berry: cool-start, FC-19;
            re-tipped bins inherit theirs). Only shown when it differs from the
            pack time — a berry pallet that spent days cooling/holding must not
            read "packed 2h ago" as its whole story (FCX-08). */}
        {pallet.born_date && (!pallet.packed_at ||
          new Date(pallet.packed_at).getTime() - new Date(pallet.born_date).getTime() > 3_600_000) && (
          <span className="text-amber-300/90">fruit age {packedAgeLabel(pallet.born_date).replace(' ago', '')}</span>
        )}
      </div>

      {hasDispatchChips && (
        <div className="flex gap-3 mt-0.5 text-xs flex-wrap">
          {pallet.intake_no && <span className="text-emerald-300 font-mono">Intake {pallet.intake_no}</span>}
          {pallet.truck_consignment_number && <span className="text-cyan-300 font-mono">Cons {pallet.truck_consignment_number}</span>}
          {pallet.ppecb_consignment_number && <span className="text-cyan-300 font-mono">PPECB {pallet.ppecb_consignment_number}</span>}
          {pallet.consignment_no && !pallet.truck_consignment_number && !pallet.ppecb_consignment_number && (
            <span className="text-cyan-300 font-mono">Cons {pallet.consignment_no}</span>
          )}
          {pallet.mission_number && (
            <span className="text-violet-300 font-mono">
              {pallet.mission_dispatched_at ? 'Dispatched' : 'Mission'} {pallet.mission_number}
            </span>
          )}
        </div>
      )}
    </>
  )
}
